<template>
  <div class="overflow-y-hidden relative w-full h-[100lvh] bg-cw-green flex flex-col">
    <img src="@/assets/images/background.webp" alt="Background" class="absolute inset-0 w-full h-full object-cover opacity-35" />

    <div class="relative text-white heightMd:px-10 heightMd:py-5 heightSm:px-5 p-10 flex items-start justify-between w-full">
      <ScoreBox id="step10" />

      <OpponentsCards :opponents="otherPlayersProfiles" id="step2" class="absolute left-0 right-0 w-max mx-auto" />

      <PlayPauseButton id="step9" />
    </div>
    <div class="absolute heightMd:bottom-[43%] bottom-1/2 px-10 heightSm:px-5 w-full">
      <div class="relative flex items-center justify-between">
        <StockPile class="" id="step7" :isEmpty="activeDeck ? !activeDeck?.length : false">
          <p class="text-2xl heightMd:text-lg font-bold text-white">{{ activeDeck?.length || '38' }}</p>
        </StockPile>
        <div id="step4" class="p-5">
          <div class="relative flex-col items-center flex gap-4 heightMd:gap-1 max-h-[100px]">
            <div>
              <p class="-mt-4 text-3xl heightMd:text-xl heightSm:text-lg font-bold tracking-tight">Your turn</p>
            </div>

            <div class="flex gap-20 heightSm:gap-10 heightMd:gap-10">
              <BaseButton background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5" id="step5">
                <p class="w-full !text-sm md:!text-base xl:!text-xl">Make a word</p>
              </BaseButton>
              <BaseButton background-color="var(--color-cw-olive)" border-color="var(--color-cw-green-border-shadow)" class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5" id="step6">
                <p class="w-full !text-sm md:!text-base xl:!text-xl">Trade a card</p>
              </BaseButton>
            </div>
          </div>
        </div>

        <StockPile id="step8" class="!p-0">
          <SwapSign class="xl:w-10 xl:h-10 lg:h-6 lg:w-6 h-5 w-5" />
        </StockPile>
      </div>
    </div>

    <div class="absolute bottom-4 lg:bottom-12 left-1/2 -translate-x-1/2 heightMd:ml-8 lg:ml-7">
      <div class="p-4">
        <div id="step1" class="flex gap-5">
          <PlayersHand :cards="userPlayersHand || dummyCards" />
        </div>
      </div>
    </div>
    <!-- Timer -->
    <div class="absolute bottom-15 lg:bottom-36 xl:left-[2.5%] lg:left-10 heightMd:left-7">
      <div class="px-2 lg:px-2.5 xl:px-3 py-3 lg:py-3.5 xl:py-4 heightMd:px-1 heightMd:py-2.5 rounded-[10px] bg-cw-green-alt font-bold min-w-[4em] relative">
        <div class="absolute top-3 bottom-3 left-3 right-3" id="step3"></div>
        <p :class="['text-lg heightMd:text-[16px] lg:text-xl xl:text-2xl text-center text-cw-green-muted font-bold leading-[1.4]']">00:59</p>
      </div>
    </div>
    <div class="absolute top-1/2 left-1/2 translate-x-1/2">
      <BaseModal ref="modalCompRef" class="!bg-cw-dark-alt !px-16" :style="{ backgroundColor: 'var(--color-cw-dark-alt)' }" @close="closeModal" :hideCloseButton="true">
        <div class="flex flex-col -mx-8">
          <h2 class="font-bold text-lg tracking-normal mb-2">You’re set!</h2>
          <p class="text-cw-grey-2 text-sm mb-4 tracking-normal font-medium max-w-[300px]" v-if="hasActiveGame">Ready to give it a go?</p>
          <p class="text-cw-grey-2 text-sm mb-4 tracking-normal font-medium max-w-[300px]" v-else>Ready to give it a go? Invite your friends for a challenge or play against computer.</p>
          <BaseButton class="w-30 text-white max-w-[125px] text-sm flex justify-center font-bold" background-color="#1AA570" border-color="#1AA570" :no-border="true" @click="startGame">
            {{ hasActiveGame ? 'Resume Game' : 'Start Game' }}
          </BaseButton>
        </div>
      </BaseModal>
    </div>
  </div>
</template>

<script setup lang="ts">
import PlayPauseButton from '@/components/game-play/PlayPauseButton.vue'
import ScoreBox from '@/components/game-play/ScoreBox.vue'
import StockPile from '@/components/game-play/StockPile.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseModal from '@/components/base/BaseModal.vue'
import SwapSign from '@/assets/svgs/others/swap.svg'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { onMounted, useTemplateRef, onUnmounted, computed, nextTick, watch } from 'vue'
import OpponentsCards from '@/components/game-play/OpponentsCards.vue'
import { useHelpers } from '@/composables/useHelpers'
import { dummyCards } from '@/data/onboarding'
import PlayersHand from '@/components/game-play/PlayersHand.vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/app'
import { useGameStore } from '@/stores/game'
import { useUserStore } from '@/stores/user'
import { useOrientation } from '@/composables/useOrientation'
import { writeStorage } from '@/composables/useStorage'

const { sleep } = useHelpers()
const { showLearning } = storeToRefs(useAppStore())
const { gameState, userPlayersHand, activeDeck, otherPlayersProfiles } = storeToRefs(useGameStore())
const { user } = useUserStore()
const { isLandscape } = useOrientation()

const modalRef = useTemplateRef('modalCompRef')
const tourKey = `tourCompleted_${user?.username}`

const hasActiveGame = computed(() => Boolean(gameState.value))

const showModal = () => {
  modalRef.value?.open()
}

const closeModal = async () => {
  await sleep(200)
  showLearning.value = false
}

const startGame = async () => {
  modalRef.value?.close()
  await sleep(200)
  showLearning.value = false
}

let tourDestroyedByOrientation = false

const driverObj = driver({
  popoverClass: 'driverjs-theme',
  showButtons: ['next'],
  nextBtnText: 'Next',
  showProgress: true,
  progressText: '{{current}} / {{total}}',

  onDestroyed: async () => {
    await sleep(200)
    if (!tourDestroyedByOrientation) {
      showModal()
    }
  },
  stagePadding: 15,

  steps: [
    {
      element: '#step1',

      popover: {
        title: 'Your hand',
        description: 'All your cards are shown here. You can hold up to 7 cards at a time',
        side: 'top',
        align: 'start'
      }
    },
    {
      element: '#step2',
      popover: {
        title: 'Opponent’s Hand',
        description: "Your opponent's cards appear here. They can also hold up to 7 cards.",
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '#step3',
      popover: {
        title: 'Timer',
        description: 'When it’s your turn, the timer counts down. Play a word or trade a card before it runs out so you don’t miss your turn.',
        side: 'left',
        align: 'start'
      }
    },
    {
      element: '#step4',
      popover: {
        title: 'Live activities',
        description: 'This is where the action happens! Track player moves and get ready for your turn.',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '#step5',

      popover: {
        title: 'Make a word',
        description: 'Want to win? Form more words than your opponents! Hit Make a Word when you have the right cards to form PLAN, SAVE, or INVEST',
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '#step6',
      popover: {
        title: 'Trade a card',
        description: "Ask another player for a card you don't have. If they accept, answer a question correctly and trade one of your cards.",
        side: 'bottom',
        align: 'start'
      }
    },
    {
      element: '#step7',
      popover: {
        title: 'Card deck',
        description: "You get new cards from this deck every time you make a word. The deck gets smaller as cards are used, and the game ends when it's empty.",
        side: 'right',
        align: 'center'
      }
    },
    {
      element: '#step8',
      popover: {
        title: 'Discard pile',
        description: 'Drag and drop a card here to exchange it. Once you do, a new card from the deck will be added to your hand',
        side: 'left',
        align: 'center'
      }
    },
    {
      element: '#step9',
      popover: {
        title: 'Pause menu',
        description: 'The pause button gives you a few seconds to take a break, go back to the menu, or replay this tutorial. Just make sure to return before your turn comes up!',
        side: 'left',
        align: 'center'
      }
    },
    {
      element: '#step10',
      popover: {
        title: 'Scoreboard',
        description: 'Track how many points you and your opponents have. The more words you make, the higher you go on the leaderboard.',
        side: 'right',
        align: 'center'
      }
    }

    // {
    //   popover: {
    //     title: 'You’re set!',
    //     description: 'Ready to give it a go? Start a game with a bot now',
    //     side: 'top',
    //     align: 'center'
    //   }
    // }
  ]
})

watch(isLandscape, (newValue, oldValue) => {
  if (!newValue && oldValue && driverObj.isActive()) {
    tourDestroyedByOrientation = true
    driverObj.destroy()
  } else if (newValue && !oldValue) {
    tourDestroyedByOrientation = false
    setTimeout(() => {
      if (isLandscape.value) {
        driverObj.drive()
      }
    }, 100)
  }
})

onMounted(async () => {
  await sleep(500)
  await nextTick()

  if (isLandscape.value) {
    driverObj.drive()
  }
})

onUnmounted(() => {
  if (driverObj) {
    driverObj.destroy()
    writeStorage('local', tourKey, 'true')
  }
})
</script>

<style>
.driver-popover.driverjs-theme {
  background-color: var(--color-cw-dark-alt);
}

.driver-popover.driverjs-theme .driver-popover-title {
  font-size: 16px;
  color: #fff;
  font-family: Rency;
  font: bold;
}

.driver-popover.driverjs-theme .driver-popover-description {
  color: var(--color-cw-grey-2);
  font-size: 12px;
  font-family: Rency;
  letter-spacing: normal;
  font-weight: 500;
}

.driver-popover.driverjs-theme button {
  font-family: Rency;
  font-size: 12px;
  padding: 5px;
  letter-spacing: normal;
}

.driver-popover.driverjs-theme .driver-popover-arrow-side-left.driver-popover-arrow {
  border-left-color: var(--color-cw-dark-alt);
}

.driver-popover.driverjs-theme .driver-popover-arrow-side-right.driver-popover-arrow {
  border-right-color: var(--color-cw-dark-alt);
}

.driver-popover.driverjs-theme .driver-popover-arrow-side-top.driver-popover-arrow {
  border-top-color: var(--color-cw-dark-alt);
}

.driver-popover.driverjs-theme .driver-popover-arrow-side-bottom.driver-popover-arrow {
  border-bottom-color: var(--color-cw-dark-alt);
}

.driver-popover-arrow {
  border-width: 10px;
}

.driver-popover-footer {
  margin-top: 20px;
}
</style>
